import classNames from 'classnames';
import clsx from 'clsx';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import { ReactElement } from 'react';
import {
  UserHasPermission,
  UserHasPermissionToRoute,
} from 'utils/auth/roles-and-permissions';
import { stepsChecker } from 'utils/general';

import AiShortcut from '@/modules/AiAgents/components/AiShortcut';
import { useAiClickHandler } from '@/modules/AiAgents/components/AiShortcut/useAiClickHandler';
import { NextImage } from '@/shared/components/atoms';
import ChooseAsset from '@/shared/components/molecules/ChooseAsset';
import HelpCenterMenuList from '@/shared/components/molecules/SideMenu/HelpCenterMenuList';
import ProfileMenuList from '@/shared/components/molecules/SideMenu/ProfileMenuList';
import SideMenuItem from '@/shared/components/molecules/SideMenu/SideMenuItem';
import { ISideMenu } from '@/shared/components/molecules/SideMenu/types';
import { EntityType } from '@/shared/constant/enums';
import { permissions } from '@/shared/constant/roles-and-permissions';
import routes from '@/shared/constant/routes';
import { useAuth } from '@/shared/hooks/context/auth';
import { useStore } from '@/shared/hooks/context/createGenericContext';
import { IGeneral } from '@/shared/types';

import Clipboard from '~/images/shared/Clipboard.svg';
import CheckedClipboard from '~/images/shared/clipboard-text.svg';
import Clock from '~/images/shared/Clock.svg';
import CloseIcon from '~/images/shared/CloseWithoutBG.svg';
import Dollar from '~/images/shared/dollarCircle.svg';
import Farm from '~/images/shared/Farm.svg';
import Packhouse from '~/images/shared/Packhouse.svg';
import People from '~/images/shared/People.svg';
import WorkingDay from '~/images/shared/tree.svg';
import Custody from '~/images/sideMenu/Custody.svg';
import FilledCollapseIcon from '~/images/sideMenu/Filled Square Button.svg';
import Company from '~/images/sideMenu/FourDots.svg';
import HamburgerIcon from '~/images/sideMenu/Hamburger.svg';
import Home from '~/images/sideMenu/home.svg';
import AydiLogo from '~/images/sideMenu/logo.png';
import Print from '~/images/sideMenu/print.svg';
import Returns from '~/images/sideMenu/Returns.svg';
import Rocket from '~/images/sideMenu/Rocket.svg';
import Settings from '~/images/sideMenu/settings.svg';
import Settlements from '~/images/sideMenu/settlements.svg';
import Sprayer from '~/images/sideMenu/sprayer.svg';
import CollapseIcon from '~/images/sideMenu/Square Button.svg';
import Statistics from '~/images/sideMenu/statistics.svg';
import Suppliers from '~/images/sideMenu/workers.svg';

interface ISideMenuProps {
  collapseHandler: IGeneral.IVoidFunction;
  openCollapse: boolean;
  // width: string;
}

export default function SideMenu({
  collapseHandler,
  openCollapse,
}: // width,
ISideMenuProps): ReactElement {
  const {
    t,
    i18n: { language },
  } = useTranslation('navigation');
  const router = useRouter();

  const [store] = useStore((store) => store);
  const selectedAsset = store?.asset;
  const isDemoFarm = store?.isDemoFarm;

  const { user, subscriptionType, onboardingChecks } = useAuth();
  const stepsCount = stepsChecker(onboardingChecks);

  const isAr = router.locale === 'ar';

  const isOperationBundle = subscriptionType === 'Operations';

  const canViewPeriodicJobs =
    UserHasPermission({
      action: permissions.CAN_VIEW_PERIODIC_OPERATIONS,
    }) && selectedAsset?.entityTypeId === EntityType.FARM;

  const menu: ISideMenu.RouteItem[] = [
    {
      route: routes.home,
      title: t('dashboard'),
      icon: <Home />,
      clickable: true,
      className: 'demo-second-step',
      revealRoute: UserHasPermissionToRoute(routes.home),
    },
    {
      route: routes.onboarding,
      title: t('onboarding-list'),
      icon: (
        <span className='relative flex items-center justify-center'>
          <span className='absolute -top-2 rounded-md bg-error-400 px-[0.15rem] text-8 text-white ltr:-right-2 rtl:-left-2'>
            {stepsCount === 8 ? 8 : 8 - stepsCount}
          </span>
          <Rocket />
        </span>
      ),
      clickable: true,
      revealRoute:
        UserHasPermissionToRoute(routes.onboarding) &&
        stepsCount < 8 &&
        selectedAsset?.entityTypeId === EntityType.FARM &&
        !isDemoFarm,
    },
    {
      route: routes.statistics,
      title: t('farm-analytics'),
      icon: <Statistics />,
      clickable: !isOperationBundle,
      className: 'demo-third-step',
      revealRoute:
        UserHasPermissionToRoute(routes.statistics) &&
        selectedAsset?.entityTypeId === EntityType.FARM,
    },
    {
      route: routes.workforce,
      title: t('workforce'),
      icon: <Suppliers />,
      clickable: true,
      revealRoute: UserHasPermissionToRoute(routes.suppliers),
      gap: true,
      nestedMenu: [
        {
          route: routes.suppliers,
          title: t('crews'),
          icon: <Suppliers />,
          clickable: true,
        },
        {
          route: routes.workersTimesheet,
          title: t('workers-timesheet'),
          icon: <Clock />,
          clickable: true,
        },
        {
          route: routes.contracts,
          title: t('contracts'),
          icon: <Suppliers />,
          clickable: !isOperationBundle,
        },
      ],
    },
    {
      route: routes.workingDay,
      title: t('technical-operations'),
      icon: <WorkingDay />,
      clickable: true,
      revealRoute: UserHasPermissionToRoute(routes.workingDay),
    },
    {
      icon: <Sprayer />,
      clickable: true,
      title: t('periodic-operations'),
      route: `${routes.periodicJobs}`,
      revealRoute: canViewPeriodicJobs,
    },
    {
      route: routes.settlements,
      title: t('finance'),
      icon: <Dollar />,
      clickable: true,
      className: 'demo-fourth-step',
      revealRoute: UserHasPermissionToRoute(routes.settlements),
      gap: true,
      nestedMenu: [
        {
          route: routes.workingDayReconciliation,
          title: t('daily-dues'),
          icon: <Dollar />,
          clickable: !isOperationBundle,
        },
        {
          route: routes.settlements,
          title: t('settlements'),
          icon: <Settlements />,
          clickable: !isOperationBundle,
        },
      ],
    },
    {
      route: routes.warehouses,
      title: t('inventory-management'),
      icon: <Packhouse />,
      clickable: !isOperationBundle,
      revealRoute: true,
      nestedMenu: [
        {
          route: routes.warehouses,
          title: t('warehouses'),
          icon: <Packhouse />,
          clickable: !isOperationBundle,
        },
        {
          route: routes.dispatchRequests,
          title: t('dispatch-requests'),
          icon: <Clipboard />,
          clickable: !isOperationBundle,
        },
        {
          route: routes.custody,
          title: t('custody'),
          icon: <Custody width='24' height='24' />,
          clickable: !isOperationBundle,
        },
        {
          route: routes.returns,
          title: t('returns'),
          icon: <Returns width='24' height='24' />,
          clickable: !isOperationBundle,
        },
      ],
    },
    {
      route: routes.printingOrders,
      title: t('printing-orders'),
      icon: <Print />,
      clickable: true,
      revealRoute: UserHasPermissionToRoute(routes.printingOrders),
    },
    {
      route: routes.aydiIntelligence,
      // TODO: feature/ai => translate
      title: t('aydi-ntelligence'),
      icon: <AiShortcut />,
      clickable: true,
      className: `!text-white ${
        openCollapse
          ? 'bg-[linear-gradient(135deg,_#3432C7_0%,_#B632C7_100%)]'
          : ''
      }`,
      revealRoute: UserHasPermissionToRoute(routes.aydiIntelligence),
      clickHandler: useAiClickHandler(),
    },
  ];

  const menuSettingsItem: ISideMenu.RouteItem = {
    route: routes.settings,
    title: t('settings'),
    icon: <Settings />,
    clickable: true,
    revealRoute: UserHasPermissionToRoute(routes.settings),
    nestedMenu: [
      {
        route: routes.companySettings,
        title: t('company'),
        icon: <Company />,
        clickable: true,
        blockedInDemo: isDemoFarm,
      },
      {
        route: routes.propertySettings,
        title: t('property'),
        icon: <Farm />,
        clickable: true,
        blockedInDemo: isDemoFarm,
      },
      {
        route: routes.companyTeamSettings,
        title: t('team'),
        icon: <People />,
        clickable: true,
        blockedInDemo: isDemoFarm,
      },
      {
        route: routes.approvalsSettings,
        title: t('approvals'),
        icon: <CheckedClipboard />,
        clickable: !isOperationBundle,
        blockedInDemo: isDemoFarm,
      },
    ],
  };

  return (
    <div
      className={clsx(
        'fixed top-0 z-30 flex w-full flex-col   border-sm border-neutralVariant-90 bg-background-neutral-99 p-16 text-24 duration-500 tablet:h-full tablet:ltr:left-0 tablet:rtl:right-0',
        openCollapse ? 'h-full tablet:w-230' : 'tablet:w-92'
      )}
    >
      <div
        className={clsx(
          'flex w-full items-center justify-between',
          openCollapse
            ? 'border-b-xs border-neutralVariant-90 pb-16 tablet:mb-32 tablet:flex-row tablet:gap-2 tablet:border-b-0 tablet:pb-0'
            : 'tablet:mb-36 tablet:flex-col tablet:gap-7'
        )}
      >
        <div className='flex items-center gap-1'>
          <div className='flex h-48 w-48 items-center justify-center overflow-hidden rounded-full bg-primary-50'>
            <NextImage
              alt='company-logo'
              src={user?.companyLogo ?? AydiLogo}
              useSkeleton
              width={48}
              height={48}
            />
          </div>
          <ChooseAsset
            arrowClassNames='text-14'
            containerClassNames={classNames(
              'border-none !text-base !text-text-default !px-5',
              openCollapse ? 'tablet:flex' : 'tablet:hidden'
            )}
            placement={isAr ? 'left' : 'right'}
            demoAssetBg='bg-white'
          />
        </div>
        <span
          className={clsx(
            'cursor-pointer text-24 text-primary-default',
            language === 'ar' && 'rotate-180'
          )}
          onClick={collapseHandler}
        >
          {openCollapse ? (
            <>
              <span className='hidden tablet:inline-block'>
                <FilledCollapseIcon />
              </span>
              <span className='tablet:hidden'>
                <CloseIcon width='20' height='20' />
              </span>
            </>
          ) : (
            <>
              <span className='hidden tablet:inline-block'>
                <CollapseIcon />
              </span>
              <span className='tablet:hidden'>
                <HamburgerIcon width='20' height='20' />
              </span>
            </>
          )}
        </span>
      </div>

      <div
        className={classNames(
          'mt-16 flex-grow flex-col gap-16 tablet:mt-0 tablet:flex tablet:justify-between',
          openCollapse ? 'flex' : 'hidden'
        )}
      >
        <ul className='flex flex-col gap-16 tablet:gap-1'>
          {menu
            .filter((m) => m.revealRoute)
            .map((item) => (
              <SideMenuItem
                key={item.route}
                item={item}
                openCollapse={openCollapse}
              />
            ))}
        </ul>
        <ul className='flex flex-col gap-16 tablet:gap-0'>
          <HelpCenterMenuList openCollapse={openCollapse} />
          {UserHasPermissionToRoute(routes.settings) && (
            <SideMenuItem item={menuSettingsItem} openCollapse={openCollapse} />
          )}
          <ProfileMenuList openCollapse={openCollapse} />
        </ul>
      </div>
    </div>
  );
}
